import { ActionsObservable, StateObservable } from 'redux-observable';
import { EMPTY, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { isActionOf } from 'typesafe-actions';
import { AppState } from '../../app-state';
import { AppEpic } from '../../common/types/app-epic.type';
import { setSourceDocument } from '../../documents/actions/source-document.actions';
import { AppActions } from '../../root.actions';
import { fetchAnnotations } from '../actions/annotations.actions';
import {
  clearCompareAnnotations,
  clearVersionHistory,
  fetchVersionHistory,
  fetchVersionHistoryError,
  fetchVersionHistorySuccess,
  selectVersion,
  setCompareAnnotations,
  setGranularity,
  toggleDiffMode,
} from '../actions/version-history.actions';
import { filterSaveEvents } from '../selectors/version-history.selectors';
import { AnnotationGroupResource } from '../resources/annotation-group.resource';

export const fetchVersionHistoryEpic: AppEpic = (
  action$: ActionsObservable<AppActions>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    filter(isActionOf(fetchVersionHistory)),
    switchMap(action => {
      const { annotatedDocumentId } = action.payload;
      return AnnotationGroupResource.getSaveEvents(
        state$.value,
        annotatedDocumentId,
      ).pipe(
        map(response =>
          fetchVersionHistorySuccess(response.annotationSaveEvents),
        ),
        catchError(() =>
          of(fetchVersionHistoryError('Failed to load version history')),
        ),
      );
    }),
  );

export const fetchVersionHistoryOnDiffModeEpic: AppEpic = (
  action$: ActionsObservable<AppActions>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    filter(isActionOf(toggleDiffMode)),
    filter(action => action.payload.enabled),
    switchMap(() => {
      const sourceDocumentId = state$.value.annotations.sourceDocumentId;
      if (!sourceDocumentId) return EMPTY;
      const { saveEvents } = state$.value.versionHistory;
      // Only fetch if not already loaded
      if (saveEvents.length > 0) return EMPTY;
      return of(fetchVersionHistory(sourceDocumentId));
    }),
  );

export const selectVersionEpic: AppEpic = (
  action$: ActionsObservable<AppActions>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    filter(isActionOf(selectVersion)),
    switchMap(action => {
      const sourceDocumentId = state$.value.annotations.sourceDocumentId;
      if (!sourceDocumentId) return EMPTY;
      const saveEventId = action.payload.saveEventId || undefined;
      return of(fetchAnnotations(sourceDocumentId, saveEventId));
    }),
  );

/**
 * When toggling diff mode off while viewing a non-latest version,
 * return to latest.
 */
export const toggleDiffModeEpic: AppEpic = (
  action$: ActionsObservable<AppActions>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    filter(isActionOf(toggleDiffMode)),
    switchMap(action => {
      if (!action.payload.enabled) {
        const selectedId = state$.value.versionHistory.selectedSaveEventId;
        if (selectedId !== null) {
          return of(selectVersion(null));
        }
        return EMPTY;
      }
      return EMPTY;
    }),
  );

/**
 * Fetch the compare (adjacent older) version for diff display.
 * Triggers on selectVersion and toggleDiffMode.
 */
export const fetchCompareVersionEpic: AppEpic = (
  action$: ActionsObservable<AppActions>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    filter(
      isActionOf([
        selectVersion,
        toggleDiffMode,
        fetchVersionHistorySuccess,
        setGranularity,
      ]),
    ),
    switchMap(() => {
      const state = state$.value;
      const sourceDocumentId = state.annotations.sourceDocumentId;
      const {
        diffModeEnabled,
        saveEvents,
        granularity,
        selectedSaveEventId,
      } = state.versionHistory;

      if (!sourceDocumentId || !diffModeEnabled) {
        return of(clearCompareAnnotations());
      }

      // Save events not loaded yet — do nothing, fetchVersionHistorySuccess will re-trigger
      if (saveEvents.length === 0) {
        return EMPTY;
      }

      const filtered = filterSaveEvents(saveEvents, granularity);

      // Only one filtered event — no older version to compare
      if (filtered.length <= 1) {
        return of(clearCompareAnnotations());
      }

      // Find current position in the filtered list and get its predecessor
      const currentIndex =
        selectedSaveEventId === null
          ? 0
          : filtered.findIndex(
              se => se.annotationSaveEventId === selectedSaveEventId,
            );

      const olderSaveEvent =
        currentIndex >= 0 && currentIndex < filtered.length - 1
          ? filtered[currentIndex + 1]
          : null;

      if (!olderSaveEvent) {
        return of(clearCompareAnnotations());
      }

      return AnnotationGroupResource.getForSaveEvent(
        state$.value,
        sourceDocumentId,
        olderSaveEvent.annotationSaveEventId,
      ).pipe(
        map(response =>
          setCompareAnnotations(
            response.saveEvent,
            response.annotations,
            response.relations,
          ),
        ),
        catchError(() => of(clearCompareAnnotations())),
      );
    }),
  );

export const clearVersionHistoryOnDocumentChangeEpic: AppEpic = (
  action$: ActionsObservable<AppActions>,
) =>
  action$.pipe(
    filter(isActionOf(setSourceDocument)),
    map(() => clearVersionHistory()),
  );
